import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Archive, Mail, Plus, Send, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const selectCls = 'h-10 px-3 text-sm rounded-md border border-line bg-surface text-fg';
const err = (e, fallback) => toast.error(e?.response?.data?.detail || fallback);
const fmt = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' }) : '—');

const blank = { client_id: '', site_ids: [], frequency: 'daily', send_time: '07:00', day_of_week: 0, recipients: '', is_active: true };

// Sites are ticked, not chosen from a dropdown, because a schedule can cover
// several. Nothing ticked = every site for the client, which is how every
// schedule created before multi-site selection behaved.
function SitePicker({ sites, selected, disabled, onChange, testId }) {
  const toggle = (id) => onChange(selected.includes(id)
    ? selected.filter((s) => s !== id)
    : [...selected, id]);          // selection order drives tab order
  if (disabled) {
    return <p className="mt-1 text-sm text-muted h-10 flex items-center">Pick a client first</p>;
  }
  return (
    <div className="mt-1 border border-line rounded-md bg-surface max-h-32 overflow-auto p-2 space-y-1"
         data-testid={testId}>
      {sites.length === 0 && <p className="text-sm text-muted">No sites for this client</p>}
      {sites.map((s) => (
        <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer hover:text-fg">
          <input
            type="checkbox"
            checked={selected.includes(s.id)}
            onChange={() => toggle(s.id)}
            data-testid={`${testId}-${s.id}`}
          />
          {s.name}
        </label>
      ))}
      {sites.length > 0 && (
        <p className="text-xs text-muted pt-1 border-t border-line">
          {selected.length === 0
            ? 'All sites — one combined report'
            : selected.length === 1
              ? '1 site'
              : `${selected.length} sites — one email, one tab per site`}
        </p>
      )}
    </div>
  );
}

export default function GSOCReports() {
  const perms = useEffectivePermissions();
  const canEdit = perms.role === 'admin' || perms.can('gsoc_reports', 'edit');
  const canDelete = perms.role === 'admin' || perms.can('gsoc_reports', 'delete');

  const [clients, setClients] = useState([]);
  const [rows, setRows] = useState([]);
  const [runs, setRuns] = useState([]);
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(null);
  const [draftSites, setDraftSites] = useState([]);
  // Sites per client, so each existing schedule row can render its own tick list.
  const [siteCache, setSiteCache] = useState({});
  const [backup, setBackup] = useState({ backup_enabled: false, backup_time: '00:00', recipientsText: '', recent_runs: [] });
  const [backupBusy, setBackupBusy] = useState(false);

  const loadBackup = () => api.get('/gsoc/backup-settings')
    .then(({ data }) => setBackup({ ...data, recipientsText: (data.backup_recipients || []).join(', ') }))
    .catch(() => {});
  useEffect(() => { loadBackup(); }, []);

  const saveBackup = async () => {
    try {
      await api.put('/gsoc/backup-settings', {
        backup_enabled: backup.backup_enabled,
        backup_time: backup.backup_time,
        backup_recipients: backup.recipientsText.split(',').map((s) => s.trim()).filter(Boolean),
      });
      toast.success('Backup settings saved');
      loadBackup();
    } catch (e) { err(e, 'Could not save the backup settings'); }
  };

  const backupNow = async () => {
    setBackupBusy(true);
    try {
      const { data } = await api.post('/gsoc/backup-now');
      toast.success(`Backup for ${data.backup_date} sent to ${data.sent_to.length} recipient(s)`);
      if (data.failed_to?.length) toast.error(`Delivery failed for: ${data.failed_to.join(', ')}`);
      loadBackup();
    } catch (e) { err(e, 'Could not send the backup'); }
    finally { setBackupBusy(false); }
  };

  useEffect(() => {
    if (!draft.client_id) { setDraftSites([]); return; }
    api.get('/gsoc/sites', { params: { client_id: draft.client_id } })
      .then(({ data }) => setDraftSites(data)).catch(() => setDraftSites([]));
  }, [draft.client_id]);

  const load = () => {
    api.get('/gsoc/report-schedules').then(({ data }) => setRows(data.map((r) => ({ ...r, site_ids: r.site_ids || [], recipients: (r.recipients || []).join(', ') })))).catch(() => {});
    api.get('/gsoc/report-runs', { params: { limit: 25 } }).then(({ data }) => setRuns(data)).catch(() => {});
  };
  useEffect(() => {
    api.get('/gsoc/client-settings').then(({ data }) => setClients(data.filter((c) => c.activity_log_enabled))).catch(() => {});
    load();
  }, []);

  // Load the site list for every client that has a schedule, once per client.
  useEffect(() => {
    const needed = [...new Set(rows.map((r) => r.client_id))].filter((id) => id && !siteCache[id]);
    if (!needed.length) return;
    Promise.all(needed.map((id) =>
      api.get('/gsoc/sites', { params: { client_id: id } })
        .then(({ data }) => [id, data]).catch(() => [id, []])
    )).then((pairs) => setSiteCache((c) => ({ ...c, ...Object.fromEntries(pairs) })));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [rows]);

  const payload = (r) => ({
    client_id: r.client_id,
    // Empty list = every site for the client. Two or more = one email with a
    // tab per site.
    site_ids: r.site_ids || [],
    frequency: r.frequency,
    send_time: r.send_time,
    day_of_week: Number(r.day_of_week) || 0,
    recipients: (r.recipients || '').split(',').map((s) => s.trim()).filter(Boolean),
    is_active: r.is_active,
  });

  const create = async () => {
    if (!draft.client_id) return toast.error('Pick a client');
    try {
      await api.post('/gsoc/report-schedules', payload(draft));
      setDraft(blank); load(); toast.success('Schedule created');
    } catch (e) { err(e, 'Could not create the schedule'); }
  };

  const save = async (r) => {
    try { await api.put(`/gsoc/report-schedules/${r.id}`, payload(r)); toast.success('Schedule saved'); load(); }
    catch (e) { err(e, 'Could not save the schedule'); }
  };

  const remove = async (r) => {
    try { await api.delete(`/gsoc/report-schedules/${r.id}`); toast.success('Schedule deleted'); load(); }
    catch (e) { err(e, 'Could not delete the schedule'); }
  };

  const sendNow = async (r) => {
    setBusy(r.id);
    try {
      const { data } = await api.post(`/gsoc/report-schedules/${r.id}/send-now`);
      toast.success(`Sent ${data.entry_count} entr${data.entry_count === 1 ? 'y' : 'ies'} to ${data.sent_to.length} recipient(s)`);
      if (data.failed_to?.length) toast.error(`Delivery failed for: ${data.failed_to.join(', ')}`);
      load();
    } catch (e) { err(e, 'Could not send the report'); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5" data-testid="gsoc-reports-page">
      <div>
        <h1 className="page-title">GSOC Scheduled Reports</h1>
        <p className="text-sm text-fg-muted mt-1 max-w-2xl">
          Emails the client's activity log as an Excel attachment. Daily reports cover the previous full day;
          weekly reports cover the previous seven days. All times are Central.
        </p>
      </div>

      {canEdit && (
        <div className="rounded-lg border border-line bg-surface p-4 space-y-3" data-testid="gsoc-report-new">
          <p className="font-medium text-fg">New schedule</p>
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[220px] flex-1">
              <Label className="text-sm">Client</Label>
              <select className={`${selectCls} mt-1 w-full`} value={draft.client_id} data-testid="gsoc-report-client"
                      onChange={(e) => setDraft((d) => ({ ...d, client_id: e.target.value }))}>
                <option value="">Select a client...</option>
                {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.client_name}</option>)}
              </select>
            </div>
            <div className="min-w-[240px]">
              <Label className="text-sm">Sites</Label>
              <SitePicker
                sites={draftSites}
                selected={draft.site_ids}
                disabled={!draft.client_id}
                testId="gsoc-report-sites"
                onChange={(ids) => setDraft((d) => ({ ...d, site_ids: ids }))}
              />
            </div>
            <div>
              <Label className="text-sm">Frequency</Label>
              <select className={`${selectCls} mt-1`} value={draft.frequency} data-testid="gsoc-report-frequency"
                      onChange={(e) => setDraft((d) => ({ ...d, frequency: e.target.value }))}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            {draft.frequency === 'weekly' && (
              <div>
                <Label className="text-sm">Send on</Label>
                <select className={`${selectCls} mt-1`} value={draft.day_of_week} data-testid="gsoc-report-day"
                        onChange={(e) => setDraft((d) => ({ ...d, day_of_week: e.target.value }))}>
                  {DAYS.map((d, i) => <option key={d} value={i}>{d}</option>)}
                </select>
              </div>
            )}
            <div>
              <Label className="text-sm">Send at</Label>
              <Input type="time" value={draft.send_time} className="mt-1" data-testid="gsoc-report-time"
                     onChange={(e) => setDraft((d) => ({ ...d, send_time: e.target.value }))} />
            </div>
            <div className="min-w-[260px] flex-1">
              <Label className="text-sm">Recipients (comma separated)</Label>
              <Input value={draft.recipients} className="mt-1" placeholder="Leave blank to use the site's list"
                     data-testid="gsoc-report-recipients"
                     onChange={(e) => setDraft((d) => ({ ...d, recipients: e.target.value }))} />
            </div>
            <Button onClick={create} data-testid="gsoc-report-create-btn"><Plus className="w-4 h-4 mr-1" />Add Schedule</Button>
          </div>
        </div>
      )}

      <div className="space-y-2" data-testid="gsoc-report-list">
        {rows.length === 0 && <p className="text-sm text-fg-muted" data-testid="gsoc-report-empty">No scheduled reports yet.</p>}
        {rows.map((r, i) => (
          <div key={r.id} className="rounded-lg border border-line bg-surface p-4 space-y-3" data-testid={`gsoc-report-row-${i}`}>
            <div className="flex flex-wrap gap-4 items-end">
              <div className="min-w-[180px] flex-1">
                <p className="font-medium text-fg">{r.client_name}</p>
                <p className="text-xs text-fg-muted" data-testid={`gsoc-report-sites-label-${i}`}>
                  {r.site_names?.length ? r.site_names.join(' + ') : 'All sites'}
                  {r.site_names?.length > 1 ? ` · ${r.site_names.length} tabs, one email` : ''}
                </p>
                <p className="text-xs text-fg-muted">
                  Last run {fmt(r.last_run_at)}{r.last_run_entry_count != null ? ` · ${r.last_run_entry_count} entries` : ''}
                </p>
              </div>
              <div className="min-w-[220px]">
                <Label className="text-xs text-fg-muted">Sites</Label>
                <SitePicker
                  sites={siteCache[r.client_id] || []}
                  selected={r.site_ids || []}
                  disabled={!canEdit}
                  testId={`gsoc-report-sitepick-${i}`}
                  onChange={(ids) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, site_ids: ids } : x))}
                />
              </div>
              <div>
                <Label className="text-xs text-fg-muted">Frequency</Label>
                <select className={`${selectCls} mt-1`} value={r.frequency} disabled={!canEdit} data-testid={`gsoc-report-freq-${i}`}
                        onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, frequency: e.target.value } : x))}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              {r.frequency === 'weekly' && (
                <div>
                  <Label className="text-xs text-fg-muted">Send on</Label>
                  <select className={`${selectCls} mt-1`} value={r.day_of_week} disabled={!canEdit} data-testid={`gsoc-report-dow-${i}`}
                          onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, day_of_week: e.target.value } : x))}>
                    {DAYS.map((d, di) => <option key={d} value={di}>{d}</option>)}
                  </select>
                </div>
              )}
              <div>
                <Label className="text-xs text-fg-muted">Send at</Label>
                <Input type="time" value={r.send_time} disabled={!canEdit} className="mt-1 w-32"
                       onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, send_time: e.target.value } : x))} />
              </div>
              <div className="min-w-[240px] flex-1">
                <Label className="text-xs text-fg-muted">Recipients</Label>
                <Input value={r.recipients} disabled={!canEdit} className="mt-1" data-testid={`gsoc-report-recips-${i}`}
                       placeholder="Blank = use the site / client list"
                       onChange={(e) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, recipients: e.target.value } : x))} />
                {!r.recipients && r.effective_recipients?.length > 0 && (
                  <p className="text-xs text-fg-muted mt-1" data-testid={`gsoc-report-effective-${i}`}>
                    Sending to: {r.effective_recipients.join(', ')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 pb-2">
                <Switch checked={r.is_active} disabled={!canEdit} data-testid={`gsoc-report-active-${i}`}
                        onCheckedChange={(v) => setRows((l) => l.map((x) => x.id === r.id ? { ...x, is_active: v } : x))} />
                <span className="text-sm text-fg-muted">Active</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {canEdit && <Button variant="outline" size="sm" onClick={() => save(r)} data-testid={`gsoc-report-save-${i}`}>Save</Button>}
              {canEdit && (
                <Button variant="outline" size="sm" onClick={() => sendNow(r)} disabled={busy === r.id} data-testid={`gsoc-report-send-${i}`}>
                  <Send className="w-4 h-4 mr-1" />{busy === r.id ? 'Sending...' : 'Send Now'}
                </Button>
              )}
              {canDelete && (
                <Button variant="ghost" size="sm" onClick={() => remove(r)} data-testid={`gsoc-report-delete-${i}`}>
                  <Trash2 className="w-4 h-4 mr-1" />Delete
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-line bg-surface p-4 space-y-3" data-testid="gsoc-backup-card">
        <div>
          <p className="font-medium text-fg">Daily Backup</p>
          <p className="text-sm text-fg-muted">
            Emails one workbook a day — the previous full day of activity, the whole pass-on log, and every
            acknowledgement with the wording the operator was shown. Central time. A run at midnight archives
            the day that just ended; if a run is missed it is sent as soon as the service is back.
          </p>
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="flex items-center gap-2 pb-2">
            <Switch checked={backup.backup_enabled} disabled={!canEdit} data-testid="gsoc-backup-enabled"
                    onCheckedChange={(v) => setBackup((b) => ({ ...b, backup_enabled: v }))} />
            <span className="text-sm text-fg-muted">Enabled</span>
          </div>
          <div>
            <Label className="text-sm">Send at</Label>
            <Input type="time" value={backup.backup_time} disabled={!canEdit} className="mt-1 w-32"
                   data-testid="gsoc-backup-time"
                   onChange={(e) => setBackup((b) => ({ ...b, backup_time: e.target.value }))} />
          </div>
          <div className="min-w-[280px] flex-1">
            <Label className="text-sm">Backup recipients (comma separated)</Label>
            <Input value={backup.recipientsText} disabled={!canEdit} className="mt-1"
                   placeholder="gsocadmin@tricorps.com" data-testid="gsoc-backup-recipients"
                   onChange={(e) => setBackup((b) => ({ ...b, recipientsText: e.target.value }))} />
          </div>
          {canEdit && (
            <>
              <Button variant="outline" onClick={saveBackup} data-testid="gsoc-backup-save">Save</Button>
              <Button variant="outline" onClick={backupNow} disabled={backupBusy} data-testid="gsoc-backup-now">
                <Archive className="w-4 h-4 mr-1" aria-hidden="true" />{backupBusy ? 'Building...' : 'Back Up Now'}
              </Button>
            </>
          )}
        </div>
        <p className="text-xs text-fg-muted" data-testid="gsoc-backup-last">
          {backup.last_backup_at
            ? `Last backup ${fmt(backup.last_backup_at)} · ${backup.last_backup_entry_count} entries`
            : 'No backup sent yet.'}
        </p>
        {(backup.recent_runs || []).length > 0 && (
          <ul className="text-xs text-fg-muted space-y-1" data-testid="gsoc-backup-runs">
            {backup.recent_runs.slice(0, 5).map((r) => (
              <li key={r.id}>
                {r.backup_date}: {r.entry_count} entries, {r.note_count} notes, {r.ack_count} acknowledgements →{' '}
                {(r.sent_to || []).join(', ') || 'nobody'}
                {r.failed_to?.length > 0 && <span className="text-danger"> (failed: {r.failed_to.join(', ')})</span>}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="rounded-lg border border-line bg-surface overflow-hidden">
        <div className="px-4 py-2 text-sm font-medium border-b border-line flex items-center gap-2">
          <Mail className="w-4 h-4" aria-hidden="true" />Delivery history
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-sunken">
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Ran</th>
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Period</th>
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Entries</th>
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Sent to</th>
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Failed</th>
                <th className="text-left px-4 py-2 font-medium text-fg-muted">Trigger</th>
              </tr>
            </thead>
            <tbody data-testid="gsoc-report-runs">
              {runs.map((r) => (
                <tr key={r.id} className="border-b border-line-subtle">
                  <td className="px-4 py-2 whitespace-nowrap">{fmt(r.ran_at)}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{r.period_start === r.period_end ? r.period_start : `${r.period_start} → ${r.period_end}`}</td>
                  <td className="px-4 py-2 tabular-nums">{r.entry_count}</td>
                  <td className="px-4 py-2">{(r.sent_to || []).join(', ') || '—'}</td>
                  <td className="px-4 py-2 text-danger">{(r.failed_to || []).join(', ') || '—'}</td>
                  <td className="px-4 py-2 text-fg-muted">{r.triggered_by?.startsWith('manual') ? 'Manual' : 'Schedule'}</td>
                </tr>
              ))}
              {runs.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-fg-muted" data-testid="gsoc-runs-empty">No reports sent yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
